/**
 * Inquiry Files API Repository
 *
 * 問い合わせに紐づくファイル（PDF等）のURL取得を担当
 */

import { ENV } from '../../../shared/config/env';
import { createApiClient } from '../../../shared/services/api';
import type { ApiResult } from '../../../shared/types/api';

// 環境変数からAPIベースURLを取得（未設定時はモック）
const API_BASE_URL = ENV.API_BASE_URL;
const USE_MOCK = !API_BASE_URL;

/**
 * デバッグログをFlutter WebViewに送信
 */
const sendDebugLog = (message: string): void => {
  if (!ENV.DEBUG) return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const webview = (window as any).DebugChannel;
    if (webview?.postMessage) {
      webview.postMessage(message);
    }
    console.log('[InquiryFiles]', message);
  } catch {
    // WebView外での実行時は無視
  }
};

/**
 * ファイル種別（API側のINQUIRY_FILE_TYPEに準拠）
 * - estimate: 見積書関連（調査報告書、見積書）
 * - reportAfter: 作業報告書関連（作業報告書、請求書、保証書）
 */
export type FileType = 'estimate' | 'reportAfter' | 'mailSend';

/**
 * ファイル情報
 */
export interface InquiryFile {
  fileId: string;
  fileName: string;
  url: string;
}

/**
 * APIレスポンス
 */
interface FilesApiResponse {
  files: InquiryFile[];
}

/**
 * モックデータ
 * 注意: ローカルアセットパスは使用しない。
 * 本番環境ではAPIからpresigned URLが返される前提。
 * 開発時のテストでは空配列を返すか、
 * REACT_APP_MOCK_PDF_URLが設定されている場合はそのURLを使用する。
 */
const MOCK_PDF_URL = ENV.MOCK_PDF_URL;

const getMockFiles = (type: FileType): InquiryFile[] => {
  // モックPDF URLが設定されていない場合は空配列を返す
  // これにより「準備中」UIが表示される
  if (!MOCK_PDF_URL) {
    return [];
  }

  if (type === 'estimate') {
    return [
      { fileId: 'mock-1', fileName: '調査報告書.pdf', url: MOCK_PDF_URL },
      { fileId: 'mock-2', fileName: '見積書.pdf', url: MOCK_PDF_URL },
      { fileId: 'mock-3', fileName: '規約.pdf', url: MOCK_PDF_URL },
      { fileId: 'mock-4', fileName: '保証内容.pdf', url: MOCK_PDF_URL },
    ];
  }
  if (type === 'mailSend') {
    // 全書類一式（ドクター側「お客様ご確認済み書類一式」と同等）
    return [
      { fileId: 'mock-1', fileName: '調査報告書.pdf', url: MOCK_PDF_URL },
      { fileId: 'mock-2', fileName: '見積書.pdf', url: MOCK_PDF_URL },
      { fileId: 'mock-5', fileName: '作業報告書.pdf', url: MOCK_PDF_URL },
      { fileId: 'mock-9', fileName: '領収書.pdf', url: MOCK_PDF_URL },
      { fileId: 'mock-3', fileName: '規約.pdf', url: MOCK_PDF_URL },
      { fileId: 'mock-4', fileName: '保証内容.pdf', url: MOCK_PDF_URL },
    ];
  }
  // reportAfter
  return [
    { fileId: 'mock-5', fileName: '規約.pdf', url: MOCK_PDF_URL },
    { fileId: 'mock-6', fileName: '作業報告書.pdf', url: MOCK_PDF_URL },
    { fileId: 'mock-7', fileName: '請求書.pdf', url: MOCK_PDF_URL },
    { fileId: 'mock-8', fileName: '保証書.pdf', url: MOCK_PDF_URL },
  ];
};

/**
 * Inquiry Files Repository
 */
export const inquiryFilesRepository = {
  /**
   * 問い合わせに紐づくファイル一覧を取得
   * @param inquiryId 問い合わせID
   * @param type ファイル種別
   * @param token 認証トークン
   */
  getFiles: async (
    inquiryId: string,
    type: FileType,
    token?: string
  ): Promise<ApiResult<InquiryFile[]>> => {
    if (USE_MOCK) {
      sendDebugLog(`getFiles: モックモード使用 (API_BASE_URL未設定)`);
      await new Promise(resolve => setTimeout(resolve, 200));
      const files = getMockFiles(type);
      sendDebugLog(`getFiles: モックデータ返却 (${files.length}件)`);
      return { success: true, data: files };
    }

    sendDebugLog(`getFiles: API呼出 inquiryId=${inquiryId}, type=${type}, tokenあり=${!!token}`);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.get<FilesApiResponse>(
      `/api/v1/inquiries/file?inquiryId=${inquiryId}&type=${type}`
    );

    if (!result.success) {
      sendDebugLog(`getFiles: APIエラー ${JSON.stringify(result.error)}`);
      return result;
    }

    sendDebugLog(`getFiles: 成功 ${result.data.files.length}件`);
    return { success: true, data: result.data.files };
  },
};
